import { GenieStatementResponse } from 'app/services/agents/databricks-genie-ai-chat.models';

export type InsightType = 'precios' | 'surtido' | 'promos';
export type InsightPriority = 'alta' | 'media' | 'baja';

export interface InsightSuggestion {
  id: string;
  insight_id?: string | number;
  date?: Date;
  title: string;
  description: string;
  status: 'pending' | 'accepted' | 'rejected';
  type: InsightType;
  priority: InsightPriority;
  estimatedImpact?: number; // Impacto monetario estimado en $
  detailedReport?: string;
  // New fields from API
  explanation_summary?: string;
  action_type?: string;
  sql_query?: string;
  generated_at?: string;
  tags?: string[];
  suggestions_json?: string;
  tableData?: GenieStatementResponse;
}

export const MOCK_INSIGHTS_SUGGESTIONS: InsightSuggestion[] = [
  {
    id: '1',
    date: new Date('2025-12-20T10:30:00'),
    title: 'Optimizar precios de productos de alta demanda',
    description:
      'Se detectó que 5 productos tienen alta demanda y precio por debajo del mercado. Ajustar precios puede aumentar rentabilidad en un 12%.',
    status: 'pending',
    type: 'precios',
    priority: 'alta',
    estimatedImpact: 25000,
    detailedReport: `## INSIGHTS PARA MAXIMIZAR RENTABILIDAD

**Hallazgo Principal:** La categoría CERVEZAS presenta una oportunidad significativa de optimización de precios, con potencial de aumentar ingresos en más de $25,000 mediante ajustes estratégicos.

### Productos Prioritarios para Ajuste de Precios:

#### 1. AUMENTAR PRECIOS (Impacto Alto)

• **CARTA BLANCA 355 ML:** Es el producto con mayor oportunidad, representa alto volumen (16,352 unidades) pero solo 9.2% de margen. Aumentar precio de $10.00 a $11.50 podría generar **$24,528 adicionales** en ingresos.

• **INDIO y HEINEKEN 473 ML:** Aunque menor volumen, tienen márgenes sub-óptimos (21% y 18% respectivamente) comparado con el promedio de categoría (27%).

#### 2. REDUCIR PRECIOS (Estimular Demanda)

• **CORONITA EXTRA 210 ML:** Margen excesivo de 40% está limitando la demanda (solo 21 unidades). Reducir precio 10% podría aumentar volumen 25% manteniendo rentabilidad.

**Causa del Fenómeno:** La estrategia de pricing actual no está balanceada - productos de alto volumen están sub-monetizados mientras que productos premium tienen precios que limitan su penetración de mercado.

**Impacto Estimado:** Los ajustes propuestos podrían aumentar los ingresos totales en aproximadamente **14%** sin comprometer significativamente los márgenes globales.`,
  },
  {
    id: '2',
    date: new Date('2025-12-19T15:45:00'),
    title: 'Anomalía en ventas de categoría Bebidas',
    description: 'Las ventas de bebidas cayeron un 23% en la última semana. Se recomienda revisar stock y promociones.',
    status: 'pending',
    type: 'surtido',
    priority: 'alta',
    estimatedImpact: 18000,
    detailedReport: `## ANÁLISIS DE ANOMALÍA EN VENTAS

**Categoría Afectada:** Bebidas
**Magnitud de la Caída:** -23% respecto a la semana anterior

### Factores Identificados:

#### 1. Stock Insuficiente
• 3 productos principales sin disponibilidad por 4 días
• Impacto estimado: **-15% en ventas**

#### 2. Competencia Agresiva
• Promoción 2x1 en cadena competidora cercana
• Pérdida de tráfico estimada: **8-10%**

### Acciones Recomendadas:
• Revisar proceso de reabastecimiento
• Implementar promoción defensiva temporal
• Monitorear precios de competencia semanalmente`,
  },
  {
    id: '3',
    date: new Date('2025-12-18T09:15:00'),
    title: 'Reabastecimiento urgente requerido',
    description: '8 items con stock crítico (<10 unidades) y alta rotación. Priorizar orden de compra.',
    status: 'pending',
    type: 'surtido',
    priority: 'alta',
    estimatedImpact: 12500,
    detailedReport: `## ALERTA DE STOCK CRÍTICO

**Productos en Riesgo de Quiebre:** 8 items

### Detalle por Producto:

#### 1. Leche Entera 1L - Stock: 6 unidades
• Venta promedio diaria: 45 unidades
• Días hasta quiebre: **0.13 días (3 horas)**
• Prioridad: **URGENTE**

#### 2. Pan de Molde - Stock: 8 unidades
• Venta promedio diaria: 32 unidades
• Días hasta quiebre: **0.25 días (6 horas)**
• Prioridad: **URGENTE**

#### 3. Agua Mineral 500ml - Stock: 9 unidades
• Venta promedio diaria: 28 unidades
• Días hasta quiebre: **0.32 días (8 horas)**
• Prioridad: **ALTA**

### Impacto Potencial:
• Pérdida de ventas estimada: **$12,500** si no se reabastece en 24 horas
• Afectación a satisfacción del cliente: **Alta**

**Acción Recomendada:** Orden de compra inmediata con entrega express`,
  },
  {
    id: '4',
    date: new Date('2025-12-17T14:20:00'),
    title: 'Oportunidad de promoción cruzada',
    description: 'Productos complementarios con alto potencial de venta conjunta. Bundle sugerido: Café + Leche + Azúcar.',
    status: 'pending',
    type: 'promos',
    priority: 'media',
    estimatedImpact: 8500,
    detailedReport: `## OPORTUNIDAD DE CROSS-SELLING

**Bundle Sugerido:** Desayuno Completo

### Composición del Bundle:
• Café molido 250g
• Leche entera 1L
• Azúcar 1kg

### Análisis de Compra Conjunta:
• **67%** de clientes que compran café también compran leche
• **45%** compran los 3 productos en la misma visita
• Frecuencia: **3.2 veces por semana** promedio

### Propuesta de Valor:
• Precio individual: $8.50 + $3.20 + $2.80 = **$14.50**
• Precio bundle: **$12.90** (descuento 11%)
• Margen bundle: **28%** vs 25% individual

### Proyección:
• Adopción estimada: **35%** de compradores de café
• Incremento en ticket promedio: **$4.20**
• Aumento en margen total: **8%**`,
  },
  {
    id: '5',
    date: new Date('2025-12-16T11:00:00'),
    title: 'Patrón estacional detectado',
    description: 'Incremento proyectado del 30% en categoría Snacks para próximos 15 días. Aumentar stock preventivo.',
    status: 'pending',
    type: 'surtido',
    priority: 'media',
    estimatedImpact: 15000,
    detailedReport: `## ANÁLISIS DE PATRÓN ESTACIONAL

**Categoría:** Snacks
**Período Proyectado:** Próximos 15 días

### Patrón Histórico:
• Misma temporada año anterior: **+32%** en ventas
• Últimos 3 años promedio: **+28%** en ventas
• Inicio del patrón: 5 días antes de fin de mes

### Productos Más Afectados:

• **Papas fritas sal:** +45%
• **Galletas dulces:** +38%
• **Chocolates:** +35%
• **Frutos secos:** +28%

### Recomendaciones de Stock:
• Aumentar inventario base en **40%**
• Priorizar productos de alta rotación
• Coordinar con proveedores entregas adicionales
• Preparar espacio de exhibición adicional

### Oportunidad Adicional:
• Promoción "Combo Fiesta": 3x2 en snacks seleccionados
• Incremento proyectado adicional: **+12%**`,
  },
];

export function generateMockPriceTableData(): GenieStatementResponse {
  // Datos mock para la tabla de recomendaciones de precios
  const tableData: GenieStatementResponse = {
    statement_response: {
      statement_id: 'mock-statement-id',
      status: {
        state: 'SUCCEEDED',
      },
      manifest: {
        format: 'JSON_ARRAY',
        schema: {
          column_count: 3,
          columns: [
            { name: 'Producto', type_text: 'STRING', type_name: 'STRING', position: 0 },
            { name: 'Precio Actual', type_text: 'DECIMAL', type_name: 'DECIMAL', position: 1 },
            { name: 'Nuevo Precio', type_text: 'DECIMAL', type_name: 'DECIMAL', position: 2 },
          ],
        },
        total_chunk_count: 1,
        chunks: [
          {
            chunk_index: 0,
            row_offset: 0,
            row_count: 5,
            byte_count: 1024,
          },
        ],
        total_row_count: 5,
        total_byte_count: 1024,
        truncated: false,
      },
      result: {
        chunk_index: 0,
        row_offset: 0,
        row_count: 5,
        data_typed_array: [
          {
            values: [{ str: 'CARTA BLANCA 355 ML' }, { num: 10.0 }, { num: 11.5 }],
          },
          {
            values: [{ str: 'INDIO 473 ML' }, { num: 15.2 }, { num: 17.0 }],
          },
          {
            values: [{ str: 'HEINEKEN 473 ML' }, { num: 18.5 }, { num: 20.0 }],
          },
          {
            values: [{ str: 'CORONITA EXTRA 210 ML' }, { num: 12.0 }, { num: 10.8 }],
          },
          {
            values: [{ str: 'MODELO ESPECIAL 355 ML' }, { num: 11.0 }, { num: 12.5 }],
          },
        ],
      },
    },
  };

  return tableData;
}
